'use client';

/**
 * TosAcceptanceModal — H3 (Audit) — Non-dismissible "accept new ToS" gate.
 *
 * When `useTosAcceptance().needsAcceptance` is true the modal opens and
 * cannot be closed without either (a) accepting the new version, or (b)
 * logging out — there is no X / esc / outside-click dismissal. Backed
 * by `<Dialog hideClose>` plus an empty `onOpenChange` (the dialog
 * primitive does not currently support a "non-modal forced" mode but
 * the layout-level mount means there is no parent that needs the
 * imperative `setOpen(false)` path).
 *
 * The acceptance is persisted via POST `/api/tos/accept` which the hook
 * wires up; the modal owns submit state + error UX.
 */

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ScrollText } from 'lucide-react';
import { Button, Spinner, VisuallyHidden, ZenModalShell, ZenSquareBadge } from '@/components/ui';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useTosAcceptance } from '@/hooks/useTosAcceptance';

export function TosAcceptanceModal() {
  const t = useTranslations('legal.tosModal');
  const { needsAcceptance, accept, currentVersion, acceptedVersion } = useTosAcceptance();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!needsAcceptance) return null;

  const heading = acceptedVersion ? t('title_update') : t('title_initial');
  const body = acceptedVersion
    ? t('body_update', { version: currentVersion })
    : t('body_initial', { version: currentVersion });

  const handleAccept = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await accept();
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'tos_accept_failed');
    }
  };

  return (
    // The dialog is open whenever `needsAcceptance` is true; the controlled
    // `onOpenChange` is intentionally a no-op so an outside-click or
    // esc-press cannot collapse the gate. `disablePointerDismissal` blocks
    // the outside-click path before it reaches `onOpenChange` for browsers
    // that surface that signal eagerly. The `hideClose` flag removes the
    // built-in X.
    <Dialog open onOpenChange={() => undefined} disablePointerDismissal>
      <DialogContent className="max-w-md p-0" hideClose>
        <ZenModalShell
          badge={
            <ZenSquareBadge>
              <ScrollText className="size-4" aria-hidden />
            </ZenSquareBadge>
          }
          title={heading}
          subtitle={body}
        >
          <VisuallyHidden>
            <DialogTitle>{heading}</DialogTitle>
            <DialogDescription>{body}</DialogDescription>
          </VisuallyHidden>

          <div className="space-y-3">
            <p className="text-xs leading-5 text-zen-text-muted">
              {t.rich('summary', {
                termsLink: (chunks) => (
                  <Link
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-zen-text underline-offset-2 hover:underline"
                  >
                    {chunks}
                  </Link>
                ),
                privacyLink: (chunks) => (
                  <Link
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-zen-text underline-offset-2 hover:underline"
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </p>

            {error && (
              <div
                role="alert"
                className="rounded-control border border-zen-danger/40 bg-zen-danger/10 px-3 py-2 text-xs text-zen-danger"
              >
                {t('error_generic')}
              </div>
            )}

            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-end">
              <Button type="button" variant="primary" size="default" onClick={handleAccept} disabled={submitting}>
                {submitting ? <Spinner className="size-4" /> : null}
                {submitting ? t('submitting') : t('acceptCta')}
              </Button>
            </div>
          </div>
        </ZenModalShell>
      </DialogContent>
    </Dialog>
  );
}
